import { createSlice } from '@reduxjs/toolkit';
import { ABClient } from 'apiClient';
import MY_TICKET_SECTION_TYPES, { emptyTabState, TICKET_SECTION_TABS } from 'views/userTicketSection/constants';
import { normalizeTabPayload } from 'views/userTicketSection/mapTicketItem';

const DATA_KEY_BY_TYPE = TICKET_SECTION_TABS.reduce((acc, tab) => {
    acc[tab.id] = tab.dataKey;
    return acc;
}, {});

const DATA_TYPES = {
    OBJECT: {
        id: 1,
        key: 'object'
    },
    ARRAY: {
        id: 2,
        key: 'array'
    },
    STRING: {
        id: 3,
        key: 'string'
    }
};

const initialState = {
    bookingRequestData: emptyTabState(),
    internalQueriesData: emptyTabState(),
    travclanHelpData: emptyTabState(),
    isTicketSectionOpen: false,
    isInitialLoading: false,
    isLoading: false,
    requestSeq: 0,
    userAcmId: null,
    isMemberNotFound: false
};

const userTicketSection = createSlice({
    name: 'userTicketSection',
    initialState,
    reducers: {
        resetUserTicketSection() {
            return initialState;
        },
        setTicketData(state, action) {
            const { type, data, replace = true } = action.payload;
            const dataKey = DATA_KEY_BY_TYPE[type];
            if (!dataKey) return;

            const existingItems = replace ? [] : state[dataKey]?.items || [];
            state[dataKey] = normalizeTabPayload(type, data, { replace, existingItems });
        },
        setInitialLoading(state, action) {
            state.isInitialLoading = Boolean(action.payload);
        },
        setLoading(state, action) {
            state.isLoading = Boolean(action.payload);
        },
        bumpRequestSeq(state) {
            state.requestSeq += 1;
        },
        setUserAcmId(state, action) {
            state.userAcmId = action.payload || null;
        },
        toggleTicketAssignmentSection(state) {
            state.isTicketSectionOpen = !state.isTicketSectionOpen;
        },
        setData(state, action) {
            const { key, data, replace = true, type = '' } = action.payload;
            if (replace) {
                state[key] = data;
            } else if (type === DATA_TYPES.OBJECT) {
                state[key] = {
                    ...state[key],
                    ...data
                };
            } else if (type === DATA_TYPES.ARRAY) {
                state[key] = [...state[key], ...data];
            } else if (type === DATA_TYPES.STRING) {
                state[key] = `${state[key]}${data}`;
            }
        }
    }
});

export const {
    resetUserTicketSection,
    toggleTicketAssignmentSection,
    setTicketData,
    setInitialLoading,
    setLoading,
    bumpRequestSeq,
    setUserAcmId,
    setData
} = userTicketSection.actions;

/**
 * Page-1 prefetch for all tabs. InfiniteScroll must use initialLoad={false}
 * because this owns the first page.
 */
export const getUserTicketDataAcrossSection =
    (abId = null) =>
    async (dispatch) => {
        if (!abId) {
            dispatch(setData({ key: 'isMemberNotFound', data: true, replace: true }));
            return;
        }
        dispatch(setInitialLoading(true));
        try {
            dispatch(setData({ key: 'isMemberNotFound', data: false, replace: true }));
            dispatch(setUserAcmId(abId));

            const [internalQueriesData, travclanHelpData, bookingRequestData] = await Promise.allSettled([
                ABClient.get(`acm/oc-system/?page=1&query_type=2&assigned_to=${abId}`),
                ABClient.get(`acm/oc-system/?page=1&query_type=1&assigned_to=${abId}`),
                ABClient.get(`bms/service-requests/?page=1&assigned_to=${abId}`)
            ]);

            if (internalQueriesData.status === 'fulfilled') {
                dispatch(
                    setTicketData({
                        type: MY_TICKET_SECTION_TYPES.INTERNAL_QUERIES,
                        data: internalQueriesData.value.data,
                        replace: true
                    })
                );
            }
            if (travclanHelpData.status === 'fulfilled') {
                dispatch(
                    setTicketData({
                        type: MY_TICKET_SECTION_TYPES.TRAVCLAN_HELP,
                        data: travclanHelpData.value.data,
                        replace: true
                    })
                );
            }
            if (bookingRequestData.status === 'fulfilled') {
                dispatch(
                    setTicketData({
                        type: MY_TICKET_SECTION_TYPES.BOOKING_REQUEST,
                        data: bookingRequestData.value.data,
                        replace: true
                    })
                );
            }
        } catch (error) {
            console.error(error);
        } finally {
            dispatch(setInitialLoading(false));
        }
    };

/**
 * DRF `next` sometimes drops `assigned_to`. Keep the logged-in ACM id on every page.
 */
const withAssignedTo = (nextUrl = '', abId = null) => {
    if (!nextUrl || !abId) return nextUrl;
    try {
        const isAbsolute = /^https?:\/\//i.test(nextUrl);
        const url = isAbsolute ? new URL(nextUrl) : new URL(nextUrl, window.location.origin);
        if (!url.searchParams.get('assigned_to')) {
            url.searchParams.set('assigned_to', String(abId));
        }
        if (isAbsolute) return url.toString();
        return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
        const joiner = nextUrl.includes('?') ? '&' : '?';
        if (nextUrl.includes('assigned_to=')) return nextUrl;
        return `${nextUrl}${joiner}assigned_to=${encodeURIComponent(abId)}`;
    }
};

/**
 * Append-only next page for any ticket tab.
 * Guarded by isLoading / hasMore — mirrors HotelAddChangeModal loadMore.
 */
export const fetchTabTicketsNext =
    (type = MY_TICKET_SECTION_TYPES.BOOKING_REQUEST) =>
    async (dispatch, getState) => {
        if (!DATA_KEY_BY_TYPE[type]) return;

        const state = getState().userTicketSectionReducer;
        const { isLoading, isInitialLoading, requestSeq, userAcmId } = state;
        const dataKey = DATA_KEY_BY_TYPE[type];
        const tabData = state[dataKey] || emptyTabState();

        if (isLoading || isInitialLoading) return;
        if (!tabData.hasMore || !tabData.next) return;
        if (!userAcmId) return;

        const seq = requestSeq + 1;
        dispatch(bumpRequestSeq());
        dispatch(setLoading(true));

        try {
            // DRF `next` is usually absolute; axios ignores baseURL for absolute URLs
            const response = await ABClient.get(withAssignedTo(tabData.next, userAcmId));
            const latestSeq = getState().userTicketSectionReducer.requestSeq;
            if (seq !== latestSeq) return;

            dispatch(
                setTicketData({
                    type,
                    data: response.data,
                    replace: false
                })
            );
        } catch (error) {
            console.error(error);
        } finally {
            const latestSeq = getState().userTicketSectionReducer.requestSeq;
            if (seq === latestSeq) {
                dispatch(setLoading(false));
            }
        }
    };

const userTicketSectionReducer = userTicketSection.reducer;

export default userTicketSectionReducer;
